import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';

import {
  CourseMaterial,
  CourseMaterialCategoryLabel,
  CourseMaterialCategory,
  CreateCourseMaterialDto,
  UpdateCourseMaterialDto,
} from '../../../../core/interfaces/course-material';
import { ProfessorCourseMaterialService } from '../../../../core/services/professor-course-material.service';
import { CourseMaterialCardComponent } from '../../../ui/views/course-material-card/course-material-card.component';
import { MaterialPreviewComponent } from '../../../ui/views/material-preview/material-preview.component';
import { SearchableSelectComponent, SelectItem } from '../../../ui/searchable-select/searchable-select.component';
import { LearningService } from '../../../../core/services/learning-path.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ExternalNavigationService } from '../../../../core/services/utils/external-navigation.service';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import { UploadPurpose, UploadService } from '../../../../core/services/uploads/upload.service';

interface CategoryFilter {
  key: 'ALL' | CourseMaterialCategoryLabel;
  i18nKey: string;
  icon: string;
}

const CATEGORY_FILTERS: CategoryFilter[] = [
  { key: 'ALL',      i18nKey: 'LIBRARY.FILTER_ALL',         icon: 'fa-solid fa-grip' },
  { key: 'Pdf',      i18nKey: 'LIBRARY.CATEGORY.Pdf',       icon: 'fa-solid fa-file-pdf' },
  { key: 'Video',    i18nKey: 'LIBRARY.CATEGORY.Video',     icon: 'fa-solid fa-circle-play' },
  { key: 'Image',    i18nKey: 'LIBRARY.CATEGORY.Image',     icon: 'fa-solid fa-image' },
  { key: 'Slide',    i18nKey: 'LIBRARY.CATEGORY.Slide',     icon: 'fa-solid fa-file-powerpoint' },
  { key: 'Document', i18nKey: 'LIBRARY.CATEGORY.Document',  icon: 'fa-solid fa-file-lines' },
  { key: 'Audio',    i18nKey: 'LIBRARY.CATEGORY.Audio',     icon: 'fa-solid fa-headphones' },
  { key: 'Other',    i18nKey: 'LIBRARY.CATEGORY.Other',     icon: 'fa-solid fa-paperclip' },
];

const CATEGORY_ENUM_OPTS: { label: string; value: CourseMaterialCategory }[] = [
  { label: 'PDF',      value: CourseMaterialCategory.Pdf },
  { label: 'Vidéo',   value: CourseMaterialCategory.Video },
  { label: 'Image',   value: CourseMaterialCategory.Image },
  { label: 'Slides',  value: CourseMaterialCategory.Slide },
  { label: 'Document',value: CourseMaterialCategory.Document },
  { label: 'Audio',   value: CourseMaterialCategory.Audio },
  { label: 'Autre',   value: CourseMaterialCategory.Other },
];

@Component({
  selector: 'app-my-library',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CourseMaterialCardComponent,
    MaterialPreviewComponent,
    SearchableSelectComponent,
  ],
  templateUrl: './my-library.component.html',
  styleUrl: './my-library.component.css',
})
export class MyLibraryComponent implements OnInit {
  private readonly service = inject(ProfessorCourseMaterialService);
  private readonly learning = inject(LearningService);
  private readonly uploads = inject(UploadService);
  private readonly auth = inject(AuthService);
  private readonly navigation = inject(ExternalNavigationService);

  /** Compte Keycloak courant : sert a restreindre la taxonomie au professeur. */
  private userId: string | null = null;

  /**
   * Inclure les supports archives dans la liste.
   *
   * <p>Ils etaient invisibles : un support range par erreur ne se retrouvait
   * plus, et l'enregistrement d'une seance archivee semblait perdu. On les
   * montre a la demande, distingues par une etiquette.</p>
   */
  readonly showArchived = signal(false);

  /**
   * Les trois panneaux sont des `<dialog>` natifs ouverts par `showModal()` :
   * couche supérieure, voile par `::backdrop`, piégeage du focus et fermeture
   * par Échap fournis par le navigateur.
   */
  @ViewChild('uploadDialog') private uploadDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('editDialog') private editDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('deleteDialog') private deleteDialog?: ElementRef<HTMLDialogElement>;

  /** Taxonomie (parcours → matières → thèmes) pour les listes du formulaire. */
  readonly paths = signal<LearningPath[]>([]);

  readonly categoryFilters = CATEGORY_FILTERS;
  readonly categoryEnumOpts = CATEGORY_ENUM_OPTS;

  // ─── List state ─────────────────────────────────────────────
  readonly materials = signal<CourseMaterial[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly selectedCategory = signal<CategoryFilter['key']>('ALL');
  readonly query = signal('');

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const cat = this.selectedCategory();
    return this.materials().filter((m) => {
      if (cat !== 'ALL' && m.category !== cat) return false;
      if (!q) return true;
      return [m.title, m.description, m.subjectName, m.themeTitle]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  });

  // ─── Upload modal ────────────────────────────────────────────
  readonly uploadOpen = signal(false);
  readonly uploading = signal(false);
  readonly uploadError = signal<string | null>(null);
  /** Avancement du dépôt direct, en pourcentage. `null` hors transfert. */
  readonly uploadPercent = signal<number | null>(null);
  uploadForm: CreateCourseMaterialDto = this.emptyForm();
  selectedFile: File | null = null;

  // ─── Edit modal ──────────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly saving = signal(false);
  editTarget: CourseMaterial | null = null;
  editForm: UpdateCourseMaterialDto = {};

  // ─── Delete confirm ──────────────────────────────────────────
  readonly deleteTarget = signal<CourseMaterial | null>(null);
  readonly deleting = signal(false);

  // ─── Aperçu ──────────────────────────────────────────────────
  readonly previewTarget = signal<CourseMaterial | null>(null);

  /** Le chemin de téléchargement dépend du rôle : ici celui du professeur. */
  readonly downloadFn = (id: number) => this.service.download(id);

  async ngOnInit(): Promise<void> {
    this.loadMaterials();
    // L'identite conditionne le perimetre : on l'attend avant de charger les
    // parcours, sinon le professeur verrait fugitivement tout le referentiel.
    this.userId = await this.auth.getUserId();
    this.loadPaths();
  }

  /** Matières du parcours choisi (cascade du formulaire d'ajout). */
  get availableSubjects(): { id: number; name: string; themes?: unknown[] }[] {
    const pid = Number(this.uploadForm.learningPathId);
    return (
      (this.paths().find((p) => p.id === pid)?.subjects as {
        id: number;
        name: string;
        themes?: unknown[];
      }[]) ?? []
    );
  }

  /** Thèmes de la matière choisie. */
  get availableThemes(): { id: number; title: string }[] {
    const sid = Number(this.uploadForm.subjectId);
    return (
      (this.availableSubjects.find((s) => s.id === sid)?.themes as {
        id: number;
        title: string;
      }[]) ?? []
    );
  }

  /** Les trois listes du formulaire, au format du sélecteur recherchable. */
  get pathOptions(): SelectItem[] {
    return this.paths().map((p) => ({ id: p.id, label: p.title }));
  }

  get subjectOptions(): SelectItem[] {
    return this.availableSubjects.map((s) => ({ id: s.id, label: s.name }));
  }

  get themeOptions(): SelectItem[] {
    return this.availableThemes.map((t) => ({ id: t.id, label: t.title }));
  }

  onUploadPathChange(id: number | null): void {
    this.uploadForm.learningPathId = id;
    this.uploadForm.subjectId = null;
    this.uploadForm.themeId = null;
  }

  onUploadSubjectChange(id: number | null): void {
    this.uploadForm.subjectId = id;
    this.uploadForm.themeId = null;
  }

  setCategory(key: CategoryFilter['key']): void { this.selectedCategory.set(key); }
  onQueryInput(v: string): void { this.query.set(v); }
  resetFilters(): void { this.selectedCategory.set('ALL'); this.query.set(''); }

  // ── Upload ──────────────────────────────────────────────────
  openUpload(): void {
    this.uploadForm = this.emptyForm();
    this.selectedFile = null;
    this.uploadError.set(null);
    this.uploadOpen.set(true);
    this.show(this.uploadDialog);
  }
  /**
   * Fermer pendant un transfert rouvrirait un formulaire vierge que la fin de
   * l'envoi viendrait refermer toute seule. Le voile et la croix passent par
   * ici, pas seulement le bouton d'annulation.
   */
  closeUpload(): void {
    if (this.uploading()) return;
    this.uploadDialog?.nativeElement.close();
  }

  onUploadClosed(): void {
    this.uploadOpen.set(false);
    this.uploadPercent.set(null);
  }

  /** Échap ne doit pas interrompre un transfert déjà lancé. */
  onUploadCancel(event: Event): void {
    if (this.uploading()) event.preventDefault();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  submitUpload(): void {
    // learningPathId à null est un choix explicite (« tous les parcours ») ;
    // seul 0, la valeur du placeholder, signale qu'on n'a rien choisi.
    const pathChosen = this.uploadForm.learningPathId !== 0;
    if (!this.uploadForm.title || !pathChosen) {
      this.uploadError.set('LIBRARY.UPLOAD.MISSING_FIELDS');
      return;
    }
    if (!this.selectedFile && !this.uploadForm.externalUrl?.trim()) {
      this.uploadError.set('LIBRARY.UPLOAD.NEED_FILE_OR_LINK');
      return;
    }

    this.uploading.set(true);
    this.uploadError.set(null);
    this.uploadPercent.set(null);

    if (this.selectedFile) {
      this.uploadPickedFile(this.selectedFile);
      return;
    }

    // Lien externe : rien à téléverser, le multipart historique suffit.
    const fd = new FormData();
    Object.entries(this.uploadForm).forEach(([k, v]) => {
      if (v != null && v !== '') fd.append(k, String(v));
    });

    this.service.upload(fd).subscribe({
      next: (res) => this.onUploaded(res?.data ?? null),
      error: () => this.onUploadFailed(),
    });
  }

  /**
   * Dépôt d'un fichier : il part **directement vers le stockage**, puis l'API
   * n'enregistre que sa clé.
   *
   * C'est ce qui permet à la bibliothèque d'accepter une vidéo de 2 Go. Relayé
   * par l'API, un tel fichier mobiliserait une connexion serveur et son disque
   * temporaire pendant tout le transfert, et se heurterait aux délais
   * d'expiration du proxy.
   */
  private uploadPickedFile(file: File): void {
    this.uploadPercent.set(0);

    // Le formulaire est figé ici : un transfert de plusieurs gigaoctets dure, et
    // l'enregistrement final ne doit pas dépendre de champs que la fermeture du
    // panneau aurait entre-temps réinitialisés.
    const form: CreateCourseMaterialDto = { ...this.uploadForm };

    this.uploads.uploadDirect(file, UploadPurpose.CourseMaterial).subscribe({
      next: (progress) => {
        this.uploadPercent.set(progress.percent);
        if (!progress.done || !progress.result) return;

        this.service
          .register({
            title: form.title,
            description: form.description,
            learningPathId: form.learningPathId,
            subjectId: form.subjectId,
            themeId: form.themeId,
            groupId: form.groupId,
            isPublic: form.isPublic,
            objectKey: progress.result.key,
            fileName: progress.result.originalFileName,
            contentType: progress.result.contentType,
            sizeBytes: progress.result.sizeBytes,
          })
          .subscribe({
            next: (res) => this.onUploaded(res?.data ?? null),
            error: () => this.onUploadFailed(),
          });
      },
      error: () => this.onUploadFailed(),
    });
  }

  private onUploaded(material: CourseMaterial | null): void {
    if (material) this.materials.update((list) => [material, ...list]);
    this.uploading.set(false);
    this.uploadPercent.set(null);
    this.uploadDialog?.nativeElement.close();
  }

  private onUploadFailed(): void {
    this.uploadError.set('LIBRARY.UPLOAD.ERROR');
    this.uploading.set(false);
    this.uploadPercent.set(null);
  }

  // ── Edit ────────────────────────────────────────────────────
  openEdit(m: CourseMaterial): void {
    this.editTarget = m;
    this.editForm = { title: m.title, description: m.description ?? '', isPublic: m.isPublic, externalUrl: m.externalUrl ?? '' };
    this.editOpen.set(true);
    this.show(this.editDialog);
  }
  closeEdit(): void { this.editDialog?.nativeElement.close(); }

  onEditClosed(): void { this.editOpen.set(false); this.editTarget = null; }

  submitEdit(): void {
    if (!this.editTarget) return;
    this.saving.set(true);
    this.service.updateMeta(this.editTarget.id, this.editForm).subscribe({
      next: (res) => {
        if (res?.data) {
          this.materials.update((list) =>
            list.map((m) => (m.id === this.editTarget!.id ? res.data! : m)),
          );
        }
        this.saving.set(false);
        this.closeEdit();
      },
      error: () => this.saving.set(false),
    });
  }

  // ── Archive/Delete ──────────────────────────────────────────
  openDelete(m: CourseMaterial): void {
    this.deleteTarget.set(m);
    this.show(this.deleteDialog);
  }

  cancelDelete(): void { this.deleteDialog?.nativeElement.close(); }

  onDeleteClosed(): void { this.deleteTarget.set(null); }

  // ─── Plomberie commune aux trois dialogues ─────────────────────────

  private show(ref?: ElementRef<HTMLDialogElement>): void {
    // showModal() lève si le dialogue est déjà ouvert.
    const el = ref?.nativeElement;
    if (el && !el.open) el.showModal();
  }

  /** Cible du `mousedown` en cours — voir `onBackdropClick`. */
  private pressOrigin: EventTarget | null = null;

  onPressStart(event: MouseEvent): void {
    this.pressOrigin = event.target;
  }

  /**
   * Un clic sur le fond d'un `<dialog>` a pour cible le dialogue lui-même. On
   * exige que le geste ait commencé là, pour qu'une sélection de texte relâchée
   * en dehors du panneau ne referme pas le formulaire.
   */
  onBackdropClick(event: MouseEvent, dialog: HTMLDialogElement): void {
    const startedOnBackdrop = this.pressOrigin === dialog || this.pressOrigin === null;
    this.pressOrigin = null;
    if (event.target !== dialog || !startedOnBackdrop) return;
    if (dialog === this.uploadDialog?.nativeElement && this.uploading()) return;
    dialog.close();
  }

  confirmDelete(): void {
    const m = this.deleteTarget();
    if (!m) return;
    this.deleting.set(true);
    this.service.archive(m.id).subscribe({
      next: () => {
        this.materials.update((list) => list.filter((x) => x.id !== m.id));
        this.deleting.set(false);
        this.deleteDialog?.nativeElement.close();
      },
      error: () => this.deleting.set(false),
    });
  }

  // ── Download ────────────────────────────────────────────────
  /**
   * Telecharge un support.
   *
   * <p>Par l'URL presignee quand elle existe : le fichier part du stockage
   * directement au navigateur, qui l'ecrit au fur et a mesure. L'API n'a plus
   * a relayer le flux — une video de 1,5 Go y mourait deux fois, le serveur
   * tenant la requete ouverte pour rien et le navigateur tentant de bufferiser
   * le tout en memoire (`net::ERR_FAILED` apres une vingtaine de secondes).</p>
   *
   * <p>Le nom d'origine est deja force par la signature
   * (`Content-Disposition`), la cle etant opaque.</p>
   *
   * <p>Le relais par l'API reste le repli, pour les seuls supports herites
   * encore ranges sur le disque : eux n'ont pas de cle a signer.</p>
   */
  onDownload(m: CourseMaterial): void {
    if (m.previewUrl) {
      window.location.href = m.previewUrl;
      return;
    }

    this.service.download(m.id).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) return;
        const filename =
          this.extractFilename(res.headers.get('Content-Disposition'))
          ?? m.fileName ?? `${m.title}${m.extension ?? ''}`;
        this.saveBlob(blob, filename);
      },
    });
  }

  onOpenExternal(m: CourseMaterial): void {
    this.navigation.open(m.externalUrl);
  }

  // ─── Private ────────────────────────────────────────────────
  private loadPaths(): void {
    this.learning
      .getScopedLearningPaths(this.userId, this.auth.isAdmin(), true, true)
      .subscribe({
      next: (res) => this.paths.set(res?.data ?? []),
      error: () => this.paths.set([]),
    });
  }

  /** Bascule l'affichage des archives et recharge. */
  toggleArchived(): void {
    this.showArchived.update((v) => !v);
    this.loadMaterials();
  }

  /**
   * Diffuse un support, ou le retire de la diffusion.
   *
   * <p>C'est ici que la video d'une seance de production devient visible des
   * eleves : elle nait privee, et la notification part a la publication — pas
   * au depot, ou elle aurait annonce un contenu inouvrable.</p>
   */
  togglePublished(m: CourseMaterial): void {
    this.service.setPublished(m.id, !m.isPublic).subscribe({
      next: () => this.loadMaterials(),
      // L'intercepteur global signale deja l'echec reseau ou serveur.
      error: () => undefined,
    });
  }

  /** Remet un support archive en circulation. Ne retablit pas la diffusion. */
  unarchive(m: CourseMaterial): void {
    this.service.unarchive(m.id).subscribe({
      next: () => this.loadMaterials(),
      error: () => undefined,
    });
  }

  private loadMaterials(): void {
    this.loading.set(true);
    this.service.getMyMaterials(this.showArchived()).subscribe({
      next: (res) => {
        this.materials.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  private emptyForm(): CreateCourseMaterialDto {
    return {
      title: '',
      description: '',
      // Le type n'est plus saisi : le serveur le déduit du fichier ou du lien.
      learningPathId: 0,
      subjectId: null,
      themeId: null,
      groupId: null,
      isPublic: true,
      externalUrl: null,
    };
  }

  private extractFilename(header: string | null): string | null {
    if (!header) return null;
    const m = /filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)["']?/i.exec(header);
    return m ? decodeURIComponent(m[1]) : null;
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
